import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { useSnoozeDepartmentOptions, useSnoozeTypeOptions } from "components/SnoozeModal/hooks";
import { getSnoozeStatusOptions } from "components/SnoozeModal/util";
import { createFormHelper } from "lib/Form";
import { SNOOZE_TYPES } from "models";
import { toDate } from "utils/dateHelperFunctions";

import type { Dealer, Snooze } from "models";

type SnoozeFormProps = {
  hasValidationError: boolean;
  dealer?: Dealer | null;
  latestSnoozeItem?: Snooze | null;
};

export type SnoozeFormValues = Pick<Snooze, "snooze_status_id" | "snooze_department_id"> & {
  snooze_type_id: SNOOZE_TYPES | null;
  snooze_date: Date | null;
  note: string | null;
};

const FormHelper = createFormHelper<SnoozeFormValues>();

export const SnoozeForm = ({ latestSnoozeItem, dealer, hasValidationError }: SnoozeFormProps) => {
  const { t } = useTranslation();
  const { setValue, watch } = FormHelper.useFormContext();

  const { snoozeTypeOptions } = useSnoozeTypeOptions(dealer);
  const { snoozeStatusOptions } = getSnoozeStatusOptions(t);
  const { snoozeDepartmentOptions } = useSnoozeDepartmentOptions();

  const [snoozeType, snoozeStatus] = watch(["snooze_type_id", "snooze_status_id"]);

  useEffect(() => {
    if (latestSnoozeItem) {
      setValue("snooze_type_id", latestSnoozeItem.snooze_type_id);
      if (latestSnoozeItem.snooze_status_id) setValue("snooze_status_id", latestSnoozeItem.snooze_status_id);
      if (latestSnoozeItem.snooze_department_id) setValue("snooze_department_id", latestSnoozeItem.snooze_department_id);
      if (latestSnoozeItem.snooze_date) setValue("snooze_date", toDate(latestSnoozeItem.snooze_date));
    }
  }, [latestSnoozeItem]);

  return (
    <>
      <Form.Group>
        <Form.Field width={8} required error={hasValidationError && !snoozeType}>
          <label>{t("v8_snooze_type", "Snooze type")}</label>
          <FormHelper.Field.Dropdown
            name="snooze_type_id"
            options={snoozeTypeOptions}
            dropdownProps={{
              fluid: true,
              placeholder: t("v8_select_snooze_type", "Select snooze type"),
              required: true,
              selectOnBlur: false
            }}
          />
        </Form.Field>

        <Form.Field width={8}>
          <label>{t("v8_snooze_date", "Snooze date")}</label>
          <FormHelper.Field.DatePicker
            name="snooze_date"
            fluid
            formFieldProps={{ disabled: snoozeType === SNOOZE_TYPES.NextVisitCustomer || snoozeType === SNOOZE_TYPES.MakeNewAppointment }}
          />
        </Form.Field>
      </Form.Group>

      {(dealer?.is_snooze_status_enabled || dealer?.is_snooze_department_enabled) && (
        <Form.Group>
          {dealer.is_snooze_status_enabled && (
            <>
              <Form.Field width={8} required error={hasValidationError && !snoozeStatus}>
                <label>{t("v8_snooze_status", "Snooze status")}</label>
                <FormHelper.Field.Dropdown
                  name="snooze_status_id"
                  options={snoozeStatusOptions}
                  dropdownProps={{
                    fluid: true,
                    placeholder: t("v8_select_snooze_status", "Select snooze status"),
                    required: true,
                    selectOnBlur: false
                  }}
                />
              </Form.Field>
              {!dealer.is_snooze_department_enabled && <Form.Field width={8} />}
            </>
          )}

          {dealer.is_snooze_department_enabled && (
            <>
              <Form.Field width={8}>
                <label>{t("v8_department", "Department")}</label>
                <FormHelper.Field.Dropdown
                  name="snooze_department_id"
                  options={snoozeDepartmentOptions}
                  dropdownProps={{
                    fluid: true,
                    clearable: true,
                    placeholder: t("v8_select_department", "Select department"),
                    selectOnBlur: false
                  }}
                />
              </Form.Field>
              {!dealer.is_snooze_status_enabled && <Form.Field width={8} />}
            </>
          )}
        </Form.Group>
      )}

      <Form.Group>
        <Form.Field width={16}>
          <label>{t("v8_remarks", "Remarks")}</label>
          <FormHelper.Field.TextArea name="note" formFieldProps={{ placeholder: t("v8_type_note_here", "Type note here"), rows: 2 }} />
        </Form.Field>
      </Form.Group>
    </>
  );
};
